<template>
  <div class="bg-gray-800 text-white h-40 lg:h-16 fixed z-10 w-full flex flex-wrap">
    <nav id="header" class="w-full lg:max-w-sm flex items-center order-2 lg:order-1">
      <div class="px-2 flex justify-center w-full">
        <router-link
          v-if="authChecked"
          :to="{ name: allowMultipleExperiments ? 'app.dashboard.index' : 'app.current_experiment' }"
        >
          <img v-if="navLogoUrl" class="h-12" :src="navLogoUrl" :alt="appName"/>
          <p v-else class="text-2xl font-bold text-center text-indigo-400" v-text="appName"></p>
        </router-link>

        <a v-else href="/">
          <img v-if="navLogoUrl" class="h-12" :src="navLogoUrl" :alt="appName"/>
          <p class="text-2xl font-bold text-center text-indigo-400" v-text="appName"></p>
        </a>
      </div>
    </nav>

    <nav id="header1" class="w-auto flex-1 order-1 lg:order-2">
      <div class="flex h-full justify-between items-center">
        <div class="relative w-full max-w-3xl px-6">
          <div class="flex items-center">
            <span class="inline-block mr-2"
                  v-text="`${$tc('messages.activity', 1)}:`">
            </span>

            <MenuDropDown
              ref="userModeSwitcher"
              :title="$t(`messages.mode_title_${currentUserMode}`)"
              :show-caret="userModes.length > 1"
              button-classes="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 border border-blue-700 rounded">
              <li v-for="mode in userModes">
                <a href="javascript:;"
                   class="px-4 py-2 block text-gray-900 hover:bg-indigo-400 hover:text-white no-underline hover:no-underline"
                   v-text="$t(`messages.mode_title_${mode}`)"
                   @click.prevent="switchUserMode(mode)">
                </a>
              </li>
            </MenuDropDown>

            <button
              class="flex items-center focus:outline-none mr-8 hover:bg-blue-700 text-white py-2 px-4 border border-blue-700 rounded"
              :class="[currentUserMode === 'uploading' ? 'bg-blue-700 font-bold' : 'bg-blue-500']"
              v-if="$permission.isAllowed('accessWithAny', modesPermissions['uploading'])"
              @click.prevent="switchUserMode('uploading')"
            >
              <fa icon="upload" class="mr-3"/>

              <span class="hidden md:inline-block"
                    v-text="$t('messages.upload')">
              </span>
            </button>

            <MenuDropDown :title="$t('messages.about')">
              <li>
                <router-link
                  class="px-4 py-2 block text-gray-900 hover:bg-indigo-400 hover:text-white no-underline hover:no-underline"
                  :to="{ name: 'app.pages', params: {content: 'about'}}"
                  target="_blank"
                  v-text="appName">
                </router-link>
              </li>

              <li>
                <router-link
                  class="px-4 py-2 block text-gray-900 hover:bg-indigo-400 hover:text-white no-underline hover:no-underline"
                  :to="{ name: 'app.pages', params: {content: 'terms-and-privacy'}}"
                  target="_blank"
                  v-text="$t('messages.terms_and_privacy')">
                </router-link>
              </li>

              <li v-if="$permission.isAllowed('accessWithAny', modesPermissions['curating'])">
                <router-link
                  class="px-4 py-2 block text-gray-900 hover:bg-indigo-400 hover:text-white no-underline hover:no-underline"
                  :to="{ name: 'app.pages', params: {content: 'curators-guidelines'}}"
                  target="_blank"
                  v-text="$t('messages.curator_guidelines')">
                </router-link>
              </li>
            </MenuDropDown>

            <MenuDropDown v-if="experiment && inExperiment && experimentIsOnGoing && (experimentContents.length > 0 || experimentCanBeQuitEarly)"
                          :title="$tc('messages.experiment', 1)">
              <li v-for="content in experimentContents">
                <router-link
                  :key="`experiment_content_link${content.id}`"
                  class="px-4 py-2 block text-gray-900 hover:bg-indigo-400 hover:text-white no-underline hover:no-underline"
                  :to="{ name: 'app.experiments.contents', params: {id: experiment.id, content: content.slug}}"
                  target="_blank"
                  v-text="content.title">
                </router-link>
              </li>

              <li v-if="experimentCanBeQuitEarly">
                <a
                  class="px-4 py-2 block text-gray-900 hover:bg-indigo-400 hover:text-white no-underline hover:no-underline"
                  href="#"
                  v-text="$t('messages.quit_early')"
                  @click.prevent="quit">
                </a>
              </li>
            </MenuDropDown>

            <router-link
              v-if="tutorialUrl"
              class="px-4 py-2 block text-white no-underline hover:no-underline"
              :to="{ name: 'app.get_started' }"
              v-text="$t('messages.get_started')">
            </router-link>
          </div>
        </div>

        <div class="flex relative inline-block pr-6">
          <div class="relative text-sm">
            <button id="userButton" @click.prevent="menuClosed = !menuClosed" class="flex items-center focus:outline-none mr-3">
              <span class="hidden md:inline-block" v-text="authUser.username"></span>
              <fa icon="caret-down" class="ml-3"/>
            </button>
            <div id="userMenu"
                 :class="{invisible: menuClosed}"
                 class="bg-white nunito rounded shadow-md mt-2 absolute mt-8 top-0 right-0 w-40 overflow-auto z-30">
              <ul class="list-reset">
                <template v-if="$permission.isAllowed('accessWithAny', ['access_bo'])">
                   <li>
                    <router-link :to="{name: 'app.edit_my_profile'}"
                       class="px-4 py-2 block text-gray-900 hover:bg-indigo-400 hover:text-white no-underline hover:no-underline"
                       v-text="$t('messages.my_profile')"
                    />
                  </li>

                  <li>
                    <hr class="border-t mx-2 border-gray-400">
                  </li>

                  <li>
                    <a href="/back-office"
                    target="_blank"
                       class="px-4 py-2 block text-gray-900 hover:bg-indigo-400 hover:text-white no-underline hover:no-underline"
                       v-text="$t('messages.back_office')"
                    >
                    </a>
                  </li>

                  <li>
                    <hr class="border-t mx-2 border-gray-400">
                  </li>
                </template>

                <li>
                  <a href="javascript:;"
                     class="px-4 py-2 block text-gray-900 hover:bg-indigo-400 hover:text-white no-underline hover:no-underline"
                     v-text="$t('messages.my_gallery')"
                     @click.prevent="switchUserMode('browsing')"
                  >
                  </a>
                </li>

                <li>
                  <hr class="border-t mx-2 border-gray-400">
                </li>

                <li>
                  <a href="https://forms.gle/9bP9VPP6884Ns6367"
                     class="px-4 py-2 block text-gray-900 hover:bg-indigo-400 hover:text-white no-underline hover:no-underline"
                     v-text="$t('messages.leave_feedback')"
                     target="_blank"
                  >
                  </a>
                </li>

                <li>
                  <hr class="border-t mx-2 border-gray-400">
                </li>

                <li>
                  <a @click.prevent="logout"
                     href="#"
                     class="px-4 py-2 block text-gray-900 hover:bg-indigo-400 hover:text-white no-underline hover:no-underline"
                     v-text="$t('messages.logout')">
                  </a>
                  <form
                    id="logout-form"
                    :action="logoutAction"
                    method="POST"
                    style="display: none;"
                  >
                    <input
                      type="hidden"
                      name="_token"
                      v-model="csrfToken"
                    />
                  </form>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import {route} from '@/http/ziggy';
import includes from 'lodash/includes';
import consentForm from '@/mixins/consent-forms';
import {mapState, mapActions, mapGetters} from 'vuex';
import {show, giveConsentTo} from '@/api/consent-forms';
import permissionsPerimeter from '@/perimeters/permissions';
import MenuDropDown from '@/components/widgets/MenuDropDown.vue';

export default {
  mixins: [consentForm],

  components: {
    MenuDropDown
  },

  perimeters: [
    permissionsPerimeter
  ],

  data() {
    return {
      appName: window.config.appName,
      navLogoUrl: window.config.navLogoUrl,
      csrfToken: window.csrf_token,
      menuClosed: true,
      tutorialUrl: window.config.tutorialUrl,
    }
  },

  computed: {
    ...mapState('auth', {
      authChecked: state => state.checked,
      authUser: state => state.user,
      currentUserMode: state => state.currentUserMode,
      modesPermissions: state => state.modesPermissions,
      modesInaccessibleToMinors: state => state.modesInaccessibleToMinors,
    }),
    ...mapState('civium', {
      experiment: state => state.experiment,
      allowMultipleExperiments: state => state.allowMultipleExperiments,
    }),
    ...mapGetters('auth', [
      'availableModes',
      'hasAnyArtifactPassedCuration',
    ]),
    ...mapGetters('civium', [
      'experimentIsOnGoing',
      'experimentCanBeQuitEarly'
    ]),

    /**
     * Current user modes.
     */
    userModes() {
      return this
        .availableModes
        .filter(mode =>
          (mode === 'curating') ||
          (mode === 'browsing' && this.hasAnyArtifactPassedCuration) ||
          (mode !== 'browsing' && this.$permission.isAllowed('accessWithAny', this.modesPermissions[mode]))
        )
        .filter(mode => mode !== this.currentUserMode);
    },

    /**
     * Return experiment contents.
     */
    experimentContents() {
      return this.experiment.contents
    },

    /**
     * Will check if we are in an experiment.
     */
    inExperiment() {
      return includes(this.$route.path, 'experiments');
    },

    /**
     * Logout URL.
     *
     * @return {String}
     */
    logoutAction() {
      return route('auth.logout.process');
    }
  },

  methods: {
    ...mapActions('auth', [
      'setUserMode'
    ]),
    ...mapActions('civium', [
      'quitExperiment'
    ]),

    closeMenu(e) {
      if (!this.$el.contains(e.target)) {
        this.menuClosed = true;
      }
    },

    /**
     * Switch User mode.
     */
    async switchUserMode(mode) {
      if(await this.giveConsentToFormIfAvailable(mode)) {
        this.setUserMode({mode});
        this.$refs.userModeSwitcher.closeManually();
      }
    },

    /**
     * Show a quit early confirmation.
     */
    experimentQuitConfirmation({title, text}) {
      return this.$swal({
        title,
        text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('messages.yes'),
        cancelButtonText: this.$t('messages.no'),
        allowOutsideClick: false,
      });
    },

    /**
     * Ask use if they want to quit experiment.
     */
    quit() {
      this
        .experimentQuitConfirmation({
          title: this.$t('messages.are_you_sure'),
          text: this.$t('messages.quit_early_warning'),
        })
        .then((result) => {
          if (result.value) {
            this.quitExperiment();
          }
        });
    },

    /**
     * Logout from the App.
     */
    logout() {
      if (this.inExperiment && this.experimentIsOnGoing && this.experimentCanBeQuitEarly) {
        this
          .experimentQuitConfirmation({
            title: this.$t('messages.are_you_sure'),
            text: this.$t('messages.quit_early_warning_logout'),
          })
          .then(async (result) => {
            if (result.value) {
              await this.quitExperiment();
              document.getElementById('logout-form').submit();
            }
          });
      } else {
        document.getElementById('logout-form').submit();
      }
    },

    /**
     * Check if this mode has a consent form and give consent to it
     * before procedding further.
     *
     * @param {String} mode
     *
     * @return {Boolean}
     */
    async giveConsentToFormIfAvailable(mode) {
      let {data} = await show(this.authUser.id, mode);

      if (data.form_available && data.need_consent) {
        let consent = await this.showConsentForm(data.consent_form);

        this.setFormResponse(data.consent_form.id,consent);
        return consent;
      }

      return true;
    },

    /**
     * Give a consent to consent form.
     *
     * @param {Number} consentForm
     * @param {Boolean} consent
     */
    async setFormResponse(consentForm, consent) {
      let {data: {message}} = await giveConsentTo(this.authUser.id, consentForm, consent);

      this.$swal({
        title: message
      });
    }
  },

  mounted() {
    document.addEventListener('click', this.closeMenu)
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closeMenu)
  }
}
</script>